import { Link } from '@tanstack/react-router';
import { ArrowLeft, Check, Save } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { hirePlansState, type HirePlansView, type Plan30, type Plan90, type TemplateProgress } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SaveBar } from '../../../components/SaveBar';
import { SkeletonLines } from '../../../components/Skeleton';
import { Tabs, type TabItem } from '../../../components/Tabs';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatDateTime } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { Plan30Form, Plan90Form } from '../../hiring-templates/components/TemplateForms';
import { EarlierText, WrittenBefore } from '../../hiring-templates/components/TemplateSectionsView';
import { useHirePlans, useSaveHirePlans } from '../api/hire-plans-api';
import styles from './HirePlansPage.module.css';

/**
 * HIRE-01 (ADR 0083; owner 2026-11-01 Q5 a, Q7 a, Q8 a): one hire's 30-day and 90-day plans. The job's line manager lands here
 * from N-103 once the offer is accepted (with Offers off, once the person is Selected); HR comes from the job's Selected step.
 * It starts from what the request and the job had. Both plans must be complete before the joining can be confirmed. The Day 30
 * and Day 90 reviews at the end of each template are not built (Q7 a): they happen after Day 1.
 */

type PlanTab = 'plan30' | 'plan90';

export function HirePlansPage({ applicationId }: { readonly applicationId: string }): ReactNode {
  const query = useHirePlans(applicationId);
  if (query.isPending) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={10} />
      </div>
    );
  }
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this hire's plans" contact="HR" />;
    if (query.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  // Keyed by the saved version, so a save from another screen (a realtime hint) starts the form from what was saved.
  return <PlansScreen key={query.data.version} view={query.data} />;
}

function statusOf(progress: TemplateProgress): string {
  if (progress.isComplete) return 'Complete';
  const left = progress.total - progress.done;
  return left > 0 ? `${left} ${left === 1 ? 'box' : 'boxes'} to go` : 'A row to finish';
}

function PlansScreen({ view }: { readonly view: HirePlansView }): ReactNode {
  const save = useSaveHirePlans(view.applicationId);
  const show = useToasts((toasts) => toasts.show);
  const [plan30, setPlan30] = useState<Plan30>(view.plan30);
  const [plan90, setPlan90] = useState<Plan90>(view.plan90);
  const [tab, setTab] = useState<PlanTab>('plan30');
  const state = hirePlansState(plan30, plan90, view.managesPeople);
  const isSaved = view.updatedAt !== null;
  const disabled = !view.canEdit;

  const tabs: readonly TabItem<PlanTab>[] = [
    { value: 'plan30', label: '30-day plan', isDone: state.plan30.isComplete, after: state.plan30.isComplete ? <Check size={16} /> : <span className={styles.left}>{statusOf(state.plan30)}</span> },
    { value: 'plan90', label: '90-day plan', isDone: state.plan90.isComplete, after: state.plan90.isComplete ? <Check size={16} /> : <span className={styles.left}>{statusOf(state.plan90)}</span> },
  ];

  async function submit(): Promise<void> {
    try {
      await save.mutateAsync({ version: view.version, plan30, plan90 });
      show({
        tone: 'success',
        message: state.isComplete ? 'Plans saved. Both are complete' : 'Plans saved',
        detail: state.isComplete ? 'The joining can be confirmed once the documents are in.' : 'The joining waits until both plans are complete.',
      });
    } catch (error) {
      show({ tone: 'danger', message: 'The plans were not saved', detail: errorMessage(error) });
    }
  }

  const status = state.isComplete ? 'Both plans are complete' : isSaved ? `Saved ${formatDateTime(view.updatedAt ?? '')}${view.updatedByName ? ` by ${view.updatedByName}` : ''}` : 'Not saved yet: this is the job’s plan to start from';

  return (
    <form
      className={styles.page}
      onSubmit={(event) => {
        event.preventDefault();
        if (view.canEdit) void submit();
      }}
    >
      <header className={styles.header}>
        <div className={styles.backs}>
          <Link to="/jobs/$jobId/$tab" params={{ jobId: view.jobId, tab: 'selected' }} className={styles.back}>
            <ArrowLeft size={14} aria-hidden />
            {view.jobTitle} · {view.jobReference}
          </Link>
          {view.offerId && (
            <Link to="/offers/$offerId/joining" params={{ offerId: view.offerId }} className={styles.back}>
              The joining
            </Link>
          )}
        </div>
        <h1 className={styles.title}>30 and 90-day plans for {view.candidateName}</h1>
        <p className={styles.meta}>
          {[view.lineManagerName ? `Line manager ${view.lineManagerName}` : 'No line manager on the job yet', view.managesPeople ? 'Manages people: the Leadership add-on applies' : null].filter(Boolean).join(' · ')}
        </p>
        <p className={styles.lead}>
          Every target needs a number or a result anyone can see. Every deadline is a day number. Write what they will be able to do and how you will check it, never just
          “learn” or “understand”. Joining waits until both plans are complete.
        </p>
        {!view.canEdit && <p className={styles.readOnly}>Only the job’s line manager and HR can change these plans.</p>}
      </header>

      <Card isFlush>
        <Tabs label="The plans" tabs={tabs} value={tab} onChange={setTab}>
          <div className={styles.panel}>
            {tab === 'plan30' ? (
              <>
                {view.earlierPlan30.length > 0 && (
                  <WrittenBefore title="From the job’s brief, as free text">
                    <EarlierText text={view.earlierPlan30.map((point) => `• ${point}`).join('\n')} />
                  </WrittenBefore>
                )}
                <Plan30Form value={plan30} onChange={setPlan30} managesPeople={view.managesPeople} disabled={disabled} />
              </>
            ) : (
              <>
                {view.earlierPlan90.length > 0 && (
                  <WrittenBefore title="From the job’s brief, as free text">
                    <EarlierText text={view.earlierPlan90.map((point) => `• ${point}`).join('\n')} />
                  </WrittenBefore>
                )}
                <Plan90Form value={plan90} onChange={setPlan90} managesPeople={view.managesPeople} disabled={disabled} />
              </>
            )}
            {tab === 'plan30' && (
              <div className={styles.next}>
                <Button variant="outline" onClick={() => setTab('plan90')}>
                  Next: 90-day plan
                </Button>
              </div>
            )}
          </div>
        </Tabs>
      </Card>

      {view.canEdit && (
        <SaveBar status={status}>
          <Button type="submit" variant="solid" icon={Save} isPending={save.isPending}>
            Save plans
          </Button>
        </SaveBar>
      )}
    </form>
  );
}
